import React, { useState } from 'react';
import WorkflowTabView from '../../../components/WorkflowTabView';
import { FaBuilding, FaUsers } from 'react-icons/fa';
import { FileSignature, Calendar } from 'lucide-react';
import CompanyProfile from './CompanyProfile';
import NDASigning from './NDASigning';
import ResponsibleContacts from './ResponsibleContacts';
import ReportingPeriod from '../scope-boundaries/ReportingPeriod';
import { useTranslation } from 'react-i18next';

const OnboardingWorkflow = ({ previewOnly = false }) => {
  const { t } = useTranslation();
  
  // Define steps configuration for the shared WorkflowTabView
  const steps = [
    {
      id: 1,
      title: t('workflow.onboarding.steps.companyProfile.title'),
      description: t('workflow.onboarding.steps.companyProfile.description'),
      icon: FaBuilding,
      component: 'CompanyProfile'
    },
    {
      id: 2,
      title: t('workflow.onboarding.steps.ndaSigning.title'),
      description: t('workflow.onboarding.steps.ndaSigning.description'),
      icon: FileSignature,
      component: 'NDASigning'
    },
    {
      id: 3,
      title: t('workflow.onboarding.steps.responsibleContacts.title'),
      description: t('workflow.onboarding.steps.responsibleContacts.description'),
      icon: FaUsers,
      component: 'ResponsibleContacts'
    },
    {
      id: 4,
      title: t('workflow.onboarding.steps.reportingPeriod.title'),
      description: t('workflow.onboarding.steps.reportingPeriod.description'),
      icon: Calendar,
      component: 'ReportingPeriod'
    }
  ];
  // Load data from localStorage on component mount
  const loadFromStorage = () => {
    try {
      const savedData = localStorage.getItem('onboardingWorkflowData');
      const savedStep = localStorage.getItem('onboardingWorkflowStep');
      
      if (savedData) {
        return {
          formData: JSON.parse(savedData),
          currentStep: savedStep ? parseInt(savedStep) : 1
        };
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
    
    return {
      formData: {
        companyProfile: {},
        ndaSigning: {},
        responsibleContacts: {},
        reportingPeriod: {}
      },
      currentStep: 1
    };
  };

  const { formData: initialFormData, currentStep: initialStep } = loadFromStorage();
  
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [formData, setFormData] = useState(initialFormData);

  // Save data to localStorage
  const saveToStorage = (newFormData, newStep) => {
    try {
      localStorage.setItem('onboardingWorkflowData', JSON.stringify(newFormData));
      localStorage.setItem('onboardingWorkflowStep', newStep.toString());
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const handleStepChange = (stepId) => {
    setCurrentStep(stepId);
    saveToStorage(formData, stepId);
  };

  const handleSaveDraft = (stepData) => {
    const newFormData = {
      ...formData,
      [getStepKey(currentStep)]: stepData
    };
    setFormData(newFormData);
    saveToStorage(newFormData, currentStep);
  };

  const handleSubmit = (stepData) => {
    const newFormData = {
      ...formData,
      [getStepKey(currentStep)]: stepData
    };
    setFormData(newFormData);
    saveToStorage(newFormData, currentStep);
  };

  const handleComplete = () => {
    // This will be called from WorkflowTabView when user clicks "Complete Onboarding"
    if (currentStep === 4) {
      // Get the current data and trigger the completion
      const currentData = formData[getStepKey(currentStep)];
      handleSubmit(currentData);
      // Navigate to home after completion
      window.location.href = '/';
    }
  };

  const getStepKey = (step) => {
    switch (step) {
      case 1: return 'companyProfile';
      case 2: return 'ndaSigning';
      case 3: return 'responsibleContacts';
      case 4: return 'reportingPeriod';
      default: return 'companyProfile';
    }
  };

  const renderCurrentStep = () => {
    const currentData = formData[getStepKey(currentStep)];

    switch (currentStep) {
      case 1:
        return (
          <CompanyProfile
            onSubmit={handleSubmit}
            initialData={currentData}
          />
        );
      case 2:
        return (
          <NDASigning
            onSubmit={handleSubmit}
            initialData={currentData}
          />
        );
      case 3:
        return (
          <ResponsibleContacts
            onSave={handleSaveDraft}
            onSubmit={handleSubmit}
            initialData={currentData}
          />
        );
      case 4:
        return (
          <ReportingPeriod
            onSubmit={handleSubmit}
            initialData={currentData}
          />
        );
      default:
        return <CompanyProfile />;
    }
  };

  return (
    <WorkflowTabView 
      steps={steps}
      currentStep={currentStep}
      onStepChange={handleStepChange}
      onComplete={currentStep === 4 ? handleComplete : undefined}
      maxWidth="max-w-4xl"
      previewOnly={previewOnly}
    >
      {renderCurrentStep()}
    </WorkflowTabView>
  );
};

export default OnboardingWorkflow;
